import { euro } from '../../core/format/currency'
import type { Product } from '../../core/types/catalog'
import type { GiftTier } from '../../data/gifts'
import type { Policy } from '../../core/types/site'
import { today } from '../../core/format/dates'
import { planBoxes } from '../../core/rules/refillPlan'
import { usageLabel } from '../../core/rules/usage'
import { refillPreview, refillRules } from '../../utils/refill'

/** Zoveel producten onder "Ook voor jouw huidtype". */
export const RELATED_COUNT = 4
/** Zo lang staat "Toegevoegd" op de knop. */
export const ADDED_FEEDBACK_MS = 2200

/** Andere producten voor hetzelfde huidtype, zonder het product zelf en zonder de "samen met"-producten. */
export function relatedProducts(product: Product, products: Product[]): Product[] {
  return products
    .filter((p) => p.id !== product.id && !product.pairs.includes(p.id) && p.skinTypes.some((s) => product.skinTypes.includes(s)))
    .slice(0, RELATED_COUNT)
}

/** Eén zin onder de knop: wat dit product doet voor het volgende cadeau. */
export function giftHintFor(price: number, giftSubtotal: number, next: GiftTier | null | undefined): string | null {
  if (!next) return null
  const after = giftSubtotal + price
  return after < next.threshold ? `Met dit product nog ${euro(next.threshold - after)} tot ${next.phrase}` : `Hiermee krijg je ${next.phrase} erbij`
}

/** Regel onder "Eenmalig": inhoud en hoe lang het meegaat, anders stap en categorie. */
export function oneOffMeta(p: Product): string {
  const lasts = usageLabel(p)
  const volume = [p.volume, lasts && `gaat ${lasts} mee`].filter(Boolean).join(' · ')
  return volume || [p.step, p.category.toLowerCase()].filter(Boolean).join(' · ')
}

export type SoloRefill = { ok: true; weeks: number; qty: number; total: number } | { ok: false; minBoxValue: number }

/**
 * Slim bijvullen met alleen dit product: elke hoeveel weken een doos, hoeveel erin, en wat hij kost.
 * `ok: false` = geen enkel ritme haalt het minimum per doos (dan samen met iets anders).
 */
export function soloRefillPlan(p: Product, sub: Policy['subscription'], start = today()): SoloRefill {
  const rules = refillRules(sub)
  const preview = refillPreview([{ product: p }], sub, start)
  const weeks = preview.suggested
  if (weeks === null) return { ok: false, minBoxValue: rules.minBoxValue }
  const [box] = planBoxes(preview.itemsFor(weeks), { intervalWeeks: weeks, rules, earliest: start, count: 1 })
  return { ok: true, weeks, qty: box?.lines[0]?.qty ?? 1, total: box?.total ?? p.price }
}

/** Regel onder "Bijvullen wanneer het op is": "Elke 12 weken 2 flesjes, € 112 · altijd gratis verzending · …". */
export function refillMeta(plan: Extract<SoloRefill, { ok: true }>, perks: string): string {
  const what = plan.qty > 1 ? `${plan.qty} tegelijk` : 'een nieuwe'
  return `Elke ${plan.weeks} weken ${what}, ${euro(plan.total)} · ${perks.replace(', ', ' · ')}`
}
